import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";

import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Group,
  Input,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";

import {
  IconDownload,
  IconExternalLink,
  IconFileSearch,
  IconSearch,
} from "@tabler/icons-react";

import { useEffect, useRef, useState } from "react";
import { Userauthcontexthooks } from "../../Hooks/Userauthcontexthooks";

const Newfirtable = () => {
  const { user } = Userauthcontexthooks();

  const [data, setdata] = useState([]);
  const [loading, setloading] = useState(true);

  const dt = useRef(null);

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [filters, setFilters] = useState({
    global: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
  });

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;

    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  useEffect(() => {
    if (user) {
      const GetFirLinks = async () => {
        try {
          const res = await fetch(
            "https://cdr-backend.onrender.com/get/firlinks",
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user}`,
              },
            }
          );

          const result = await res.json();

          if (res.status === 200) {
            setdata(result.FirlinksOBJ || []);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setloading(false);
        }
      };

      GetFirLinks();
    }
  }, [user]);

  // FIR LINK DESIGN
  const firLinkBody = (rowData) => {
    return (
      <Anchor
        href={rowData.firlink}
        target="_blank"
        underline="never"
      >
        <Group gap={6} wrap="nowrap">
          <Badge
            variant="light"
            color="cyan"
            radius="xl"
            size="lg"
          >
            Open FIR Portal
          </Badge>

          <ActionIcon
            variant="light"
            color="cyan"
            radius="xl"
          >
            <IconExternalLink size={16} />
          </ActionIcon>
        </Group>
      </Anchor>
    );
  };

  const stateBody = (rowData) => {
    return (
      <Group gap="xs">
        <ThemeIcon
          variant="light"
          color="blue"
          radius="xl"
          size={34}
        >
          <IconFileSearch size={18} />
        </ThemeIcon>

        <Text fw={600} c="white" size="sm">
          {rowData.firstate}
        </Text>
      </Group>
    );
  };

  const header = (
    <Group justify="space-between" mb="md" wrap="wrap">
      <Box>
        <Title
          order={3}
          style={{
            color: "white",
            fontWeight: 800,
          }}
        >
          FIR Details Portal
        </Title>

        <Text size="sm" c="#94a3b8">
          Search FIR portal links state wise.
        </Text>
      </Box>

      <Group>
        <Input
          placeholder="Search state or FIR link..."
          radius="xl"
          size="md"
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          leftSection={<IconSearch size={18} />}
          styles={{
            input: {
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "white",
              width: 260,
              height: 46,
            },
          }}
        />

        <Tooltip label="Download FIR Records">
          <Button
            radius="xl"
            variant="light"
            color="cyan"
            leftSection={<IconDownload size={18} />}
            onClick={exportCSV}
          >
            Export
          </Button>
        </Tooltip>
      </Group>
    </Group>
  );

  return (
    <Card
      radius="28px"
      p="lg"
      mt="md"
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 10px 35px rgba(0,0,0,0.35)",
      }}
    >
      {/* TOP INFO */}
      <Paper
        radius="24px"
        p="md"
        mb="lg"
        style={{
          background:
            "linear-gradient(135deg, rgba(6,182,212,0.18), rgba(59,130,246,0.12))",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Text
              size="sm"
              fw={700}
              c="cyan"
              tt="uppercase"
            >
              Police Administration
            </Text>

            <Title order={4} c="white">
              FIR Details & Online Portals
            </Title>
          </Stack>

          <Badge
            variant="gradient"
            gradient={{ from: "cyan", to: "blue" }}
            radius="xl"
            size="xl"
          >
            {data.length} Records
          </Badge>
        </Group>
      </Paper>

      {/* TABLE */}
      <ScrollArea>
        <div style={{ minWidth: "850px" }}>
          <DataTable
            ref={dt}
            value={data}
            paginator
            rows={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            filters={filters}
            globalFilterFields={["firstate", "firlink"]}
            stripedRows
            showGridlines
            loading={loading}
            responsiveLayout="scroll"
            scrollable
            scrollHeight="520px"
            emptyMessage="No FIR records found"
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            header={header}
            tableStyle={{
              minWidth: "100%",
            }}
            style={{
              borderRadius: "20px",
              overflow: "hidden",
            }}
          >
            {/* STATE */}
            <Column
              field="firstate"
              header="State Name"
              body={stateBody}
              sortable
              style={{
                minWidth: "280px",
              }}
            />

            {/* FIR LINK */}
            <Column
              field="firlink"
              header="FIR Portal"
              body={firLinkBody}
              sortable
              style={{
                minWidth: "350px",
              }}
            />
          </DataTable>

          {/* LOADER */}
          {loading && (
            <Center mt="lg">
              <Loader color="cyan" />
            </Center>
          )}
        </div>
      </ScrollArea>

      {/* PRIME TABLE CUSTOM STYLE */}
      <style>
        {`
          .p-datatable {
            background: transparent !important;
            color: white !important;
          }

          .p-datatable .p-datatable-header {
            background: transparent !important;
            border: none !important;
          }

          .p-datatable .p-datatable-thead > tr > th {
            background: rgba(255,255,255,0.06) !important;
            color: #e2e8f0 !important;
            border-color: rgba(255,255,255,0.08) !important;
            font-weight: 700;
            padding: 16px;
          }

          .p-datatable .p-datatable-tbody > tr {
            background: transparent !important;
            color: white !important;
          }

          .p-datatable .p-datatable-tbody > tr > td {
            border-color: rgba(255,255,255,0.06) !important;
            padding: 14px 16px;
          }

          .p-datatable .p-paginator {
            background: transparent !important;
            border: none !important;
            color: white !important;
            padding-top: 18px;
          }

          .p-paginator .p-paginator-pages .p-paginator-page {
            color: white !important;
          }

          .p-dropdown {
            background: rgba(255,255,255,0.06) !important;
            border: 1px solid rgba(255,255,255,0.08) !important;
            color: white !important;
          }

          .p-dropdown-label {
            color: white !important;
          }

          .p-paginator .p-paginator-current {
            color: #cbd5e1 !important;
          }

          .p-paginator .p-link {
            color: white !important;
          }
        `}
      </style>
    </Card>
  );
};

export default Newfirtable;